import { createAction, createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { geneAsyncAction } from 'services/actionConfigs';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { ICategory, featureName, UpdateProductBody, IProductListResponse, IProduct } from './constants';

const createAsync = geneAsyncAction(featureName);

const async = {
  getCategoryList: createAsync<undefined, ICategory[]>('getCategoryList'),
  getProductList: createAsync<{ categoryId: string; page: number; size: number; search: string }, IProductListResponse>('getProductList'),
};

const deleteCategory = createAction<string>('deleteCategory');
const editCategory = createAction<{ id: string; categoryName: string }>('editCategory');
const editProduct = createAction<UpdateProductBody>('editProduct');
const selectedCategory = createAction<string | null>('selectedCategory');

export type IproductReducerState = {
  isLoading: boolean;
  categories: ICategory[];
  selectedCategory: string | null;
  productData: IProductListResponse | null;
};

type MyState = IproductReducerState;

const getCurrentState = (state: RootState): MyState => state[featureName];

const selector = <T = MyState>(key: keyof T, defaultValue?: any) => useAppSelector((state) => get(getCurrentState(state), key, defaultValue));

const selectors = {
  isLoading: () => selector('isLoading') as MyState['isLoading'],
  categories: () => selector('categories') as MyState['categories'],
  selectedCategory: () => selector('selectedCategory') as MyState['selectedCategory'],
  productData: () => selector('productData') as MyState['productData'],
};

const Slice = createSlice({
  name: featureName,
  initialState: {
    isLoading: false,
    categories: [],
    productData: null,
    error: null,
    selectedCategory: null,
  } as MyState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(async.getCategoryList.fetch, (state) => {
        state.isLoading = true;
      })
      .addCase(async.getCategoryList.success, (state, { payload }) => {
        state.categories = payload;
        state.isLoading = false;
      })
      .addCase(async.getCategoryList.fail, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteCategory, (state, action) => {
        state.categories = state.categories.filter((category) => category.id !== action.payload);
      })
      .addCase(editCategory, (state, action) => {
        state.categories = state.categories.map((category) => {
          if (category.id === action.payload.id) {
            return { ...category, categoryName: action.payload.categoryName };
          }
          return category;
        });
      })
      .addCase(selectedCategory, (state, action) => {
        state.selectedCategory = action.payload;
      })
      .addCase(async.getProductList.fetch, (state) => {
        state.isLoading = true;
      })
      .addCase(async.getProductList.success, (state, action) => {
        state.productData = action.payload;
        state.isLoading = false;
      })
      .addCase(async.getProductList.fail, (state) => {
        state.isLoading = false;
      })
      .addCase(editProduct, (state, { payload }) => {
        state.productData &&
          (state.productData = {
            ...state.productData,
            content: state.productData?.content?.map((product: IProduct) => (product.id === payload.productId ? { ...product, ...payload } : product)) || [],
          });
      });
  },
});

const actions = {
  ...Slice.actions,
  async,
  deleteCategory,
  editCategory,
  selectedCategory,
  editProduct,
};

const reducer = Slice.reducer;

export { actions, reducer, selectors };
